import { useQuery } from "@tanstack/react-query";
import { API_ROUTES } from "../../constants";
import { apiClient } from "./apiClient";
import { QUERY_KEYS } from "./constants";
import type { ParsedSource, Source, SourceResponse } from "./types";

export function deriveHost(input: string): string {
  // Extract the domain from the link to use as the label
  return input.replace(/^(?:https?:\/\/)?(?:www\.)?([^/]+).*/, "$1");
}

export function derivePath(input: string): string {
  // Extract the path from the link to use as the label
  return input.replace(/^(?:https?:\/\/)?(?:www\.)?[^/]+(\/.*)?$/, "$1");
}

/**
 * Hook to fetch sources for the current query
 */
export const useGetSources = (query: string | null) => {
  return useQuery({
    queryKey: QUERY_KEYS.getSources(query),
    queryFn: async () => {
      // You might want to pass the query as a parameter to the API call
      return await apiClient.post<SourceResponse>(
        API_ROUTES.omniSearch.sources,
        {
          content: query,
        }
      );
    },
    select: (data) => {
      let output: Source[] = [];
      try {
        const parsed: unknown = JSON.parse(data.data.content);
        if (Array.isArray(parsed)) {
          output = parsed
            // sort by score descending
            .sort((a: ParsedSource, b: ParsedSource) => b.score - a.score)
            .map((item: { link: string }) => {
              // Add runtime validation to ensure type safety
              if (typeof item === "object" && item !== null && "link" in item) {
                return {
                  link: item.link,
                  label: deriveHost(item.link),
                  path: derivePath(item.link),
                };
              }
              throw new Error("Invalid source item structure");
            });
        } else {
          throw new Error("Parsed content is not an array");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to parse sources";
        throw new Error(errorMessage);
      }
      return output;
    },
    staleTime: Infinity, // Sources do not change often, so we can cache them indefinitely
    enabled: !!query, // Only run query if query parameter exists
  });
};
